import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LifeBuoy, Siren, CalendarHeart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { EmergencyButton } from "@/components/EmergencyButton";
import { DatePicker } from "@/components/DatePicker";
import { api, errMsg, todayIST } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { WhatsAppButton } from "@/components/WhatsAppButton";

const TYPES = [["support", "General support", LifeBuoy], ["assistance", "Booking assistance", CalendarHeart], ["emergency", "Urgent safety report", Siren]];

export default function Support() {
  const [params] = useSearchParams();
  const { user } = useAuth();
  const [type, setType] = useState(params.get("type") || "support");
  const [f, setF] = useState({ name: "", email: "", subject: "", message: "", city: "Dumka", preferred_date: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const up = (k) => (e) => setF({ ...f, [k]: e.target.value });

  useEffect(() => {
    if (user) setF((x) => ({ ...x, name: user.name, email: user.email }));
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/support", { ...f, type, preferred_date: f.preferred_date || null });
      setDone(true);
      toast.success("Request received");
    } catch (e2) {
      toast.error(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="container-x py-12 grid lg:grid-cols-12 gap-10" data-testid="support-page">
      <div className="lg:col-span-5 space-y-5">
        <p className="eyebrow">Customer support</p>
        <h1 className="text-4xl sm:text-5xl font-bold">How can we help?</h1>
        <p className="text-slate-600">Ask a question, request help finding the right companion, or send an urgent safety report. Support hours 9:00–21:00 IST.</p>
        <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-5 space-y-3" data-testid="support-whatsapp-card">
          <p className="font-semibold text-emerald-900">Prefer chatting?</p>
          <p className="text-sm text-emerald-800">Message us on WhatsApp at +91 74799 13855. Please don't share passwords or ID documents in chat.</p>
          <WhatsAppButton testId="support-whatsapp-btn" />
        </div>
        <div className="rounded-2xl bg-rose-50 border border-rose-200 p-5 space-y-3">
          <p className="font-semibold text-rose-900">In immediate danger?</p>
          <p className="text-sm text-rose-800">Call 112 first. Then notify us so we can act on the booking.</p>
          <EmergencyButton />
        </div>
      </div>
      <div className="lg:col-span-7">
        {done ? (
          <div className="card-surface p-8 text-center space-y-3" data-testid="support-success"><h2 className="text-3xl font-semibold">Thank you</h2><p className="text-slate-600">Our team will reply to {f.email} shortly.</p></div>
        ) : (
          <form onSubmit={submit} className="card-surface p-6 sm:p-8 space-y-4" data-testid="support-form">
            <div className="grid grid-cols-3 gap-2">
              {TYPES.map(([k, l, I]) => (
                <button type="button" key={k} onClick={() => setType(k)} data-testid={`support-type-${k}`}
                  className={`rounded-xl border p-3 text-xs font-semibold flex flex-col items-center gap-1.5 transition-colors ${type === k ? "border-[#0D5C46] bg-emerald-50 text-[#0D5C46]" : "hover:border-slate-400"}`}><I size={18} />{l}</button>
              ))}
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Input placeholder="Your name" value={f.name} onChange={up("name")} data-testid="support-name-input" />
              <Input type="email" placeholder="Email" value={f.email} onChange={up("email")} data-testid="support-email-input" />
            </div>
            {type === "assistance" && (
              <div className="grid sm:grid-cols-2 gap-3">
                <Input placeholder="City" value={f.city} onChange={up("city")} data-testid="support-city-input" />
                <DatePicker testId="support-date-input" min={todayIST()} clearable placeholder="Preferred date (dd/mm/yyyy)" value={f.preferred_date} onChange={(v) => setF({ ...f, preferred_date: v })} />
              </div>
            )}
            <Input placeholder="Subject" value={f.subject} onChange={up("subject")} data-testid="support-subject-input" />
            <Textarea rows={5} placeholder="Message (min 10 characters)" value={f.message} onChange={up("message")} data-testid="support-message-input" />
            <Button className="w-full rounded-full h-11" disabled={busy} data-testid="support-submit-btn">{busy ? <Loader2 className="animate-spin" /> : "Send"}</Button>
          </form>
        )}
      </div>
    </div>
  );
}
